// ---------------------------------------------------------------------------
// AppointmentForm Renderer (editor key: AppointmentForm)
// ---------------------------------------------------------------------------
// Renders a static booking form (name + email + service + date) for
// service-business pages. Service options come from a newline-delimited
// textarea — duplicates and blank lines are dropped, and the list is capped
// to 24 entries. The `action` URL routes through the URL safelist; if
// omitted (or rejected by the safelist) the form publishes as `disabled` so
// the page never silently posts to `#`. Pure HTML, no JS.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface AppointmentFormProps {
  heading?: string;
  services?: string;
  submitLabel?: string;
  action?: string;
  nameLabel?: string;
  emailLabel?: string;
}

const DEFAULT_HEADING = "Book Your Appointment";
const DEFAULT_SUBMIT_LABEL = "Book Now";
const DEFAULT_NAME_LABEL = "Full name";
const DEFAULT_EMAIL_LABEL = "Email address";
const DEFAULT_SERVICE_PLACEHOLDER = "Select a service";

const MAX_SERVICES = 24;

function parseServices(value: unknown): string[] {
  if (typeof value !== "string") return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of value.split("\n")) {
    const trimmed = raw.trim();
    if (!trimmed) continue;
    if (seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
    if (out.length >= MAX_SERVICES) break;
  }
  return out;
}

function nonEmpty(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  return trimmed ? trimmed : fallback;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as AppointmentFormProps;

  const heading = nonEmpty(p.heading, DEFAULT_HEADING);
  const submitLabel = nonEmpty(p.submitLabel, DEFAULT_SUBMIT_LABEL);
  const nameLabel = nonEmpty(p.nameLabel, DEFAULT_NAME_LABEL);
  const emailLabel = nonEmpty(p.emailLabel, DEFAULT_EMAIL_LABEL);
  const services = parseServices(p.services);

  const rawAction = typeof p.action === "string" ? p.action.trim() : "";
  const sanitizedAction = rawAction ? url(rawAction) : "";
  const hasAction = sanitizedAction.length > 0 && sanitizedAction !== "#";
  const disabledAttrs = hasAction ? "" : ' disabled aria-disabled="true"';

  const formAttrs = hasAction
    ? `action="${sanitizedAction}" method="post"`
    : `data-ease-appointment-form="unconfigured"`;

  const serviceOptions = services
    .map((label) => `        <option value="${attr(label)}">${text(label)}</option>`)
    .join("\n");
  const serviceField = services.length > 0
    ? `\n      <label class="ease-appointment-form__field">
        <span class="ease-appointment-form__label">Service</span>
        <select class="ease-appointment-form__select" name="service" required${disabledAttrs}>
          <option value="">${text(DEFAULT_SERVICE_PLACEHOLDER)}</option>
${serviceOptions}
        </select>
      </label>`
    : "";

  return `<section class="ease-section ease-appointment-form">
  <div class="ease-appointment-form__inner">
    <h2 class="ease-appointment-form__heading">${text(heading)}</h2>
    <form class="ease-appointment-form__form" ${formAttrs}>
      <label class="ease-appointment-form__field">
        <span class="ease-appointment-form__label">${text(nameLabel)}</span>
        <input class="ease-appointment-form__input" type="text" name="name" autocomplete="name" required${disabledAttrs}>
      </label>
      <label class="ease-appointment-form__field">
        <span class="ease-appointment-form__label">${text(emailLabel)}</span>
        <input class="ease-appointment-form__input" type="email" name="email" autocomplete="email" required${disabledAttrs}>
      </label>${serviceField}
      <label class="ease-appointment-form__field">
        <span class="ease-appointment-form__label">Preferred date</span>
        <input class="ease-appointment-form__input" type="date" name="appointment_date" required${disabledAttrs}>
      </label>
      <button class="ease-appointment-form__submit ease-btn ease-btn-primary" type="submit"${disabledAttrs}>${text(submitLabel)}</button>
    </form>
  </div>
</section>`;
}

registerRenderer("appointment-form", { render });
